import { useCallback, useEffect, useMemo, useRef, useState, Dispatch, SetStateAction } from 'react';
import type {
  AttentionItem,
  GitHubPullRequest,
  GitHubReviewer,
  CurrentUserIdentity,
} from '../../domain/types';
import { isMatchingUser } from '../../domain/types';
import { mapGitHubPRToAttentionItem } from '../../domain/mappers';
import { gitHubClient } from '../../services/clients';
import type { GitHubMergeStrategy } from '../../services/clients/types';
import type {
  AiReviewPublishEvent,
  PublishAiReviewResult,
  PullRequestAiReview,
  AiReviewJob,
} from '@zunelo/shared';
import { applyOptimisticApproval, applyOptimisticCommentToggle } from './githubOptimistic';
import { createIdempotencyKey } from '../../services/idempotencyKey';
import {
  registerSourceSyncErrorHandler,
  registerSourceSyncReconciler,
  AI_REVIEW_SYNC_UNAVAILABLE_MESSAGE,
} from '../useSourceSyncIndex';
import { decodeSourceSyncResourceKey } from '@zunelo/shared';
import {
  projectAiReviewProgress,
  type AiReviewStatusRefreshError,
} from '../../domain/aiReviewProgress';
import {
  AI_REVIEW_STATUS_REFRESH_FAILED,
  getAiReviewJobWithRetry,
} from '../../services/aiReviewStatus';

export const AI_REVIEW_STATUS_POLL_INTERVAL_MS = 5_000;

export function activeAiReviewJobIds(states: Record<string, GitHubAiReviewState>): string[] {
  return [
    ...new Set(
      Object.values(states)
        .filter(
          (state) =>
            state.status === 'loading' &&
            state.job &&
            (state.job.status === 'queued' ||
              state.job.status === 'running' ||
              state.job.status === 'retrying'),
        )
        .map((state) => state.job!.jobId),
    ),
  ];
}

export function buildCurrentUserReviewer(currentUser?: CurrentUserIdentity): GitHubReviewer {
  const login = currentUser?.providerHandle || currentUser?.email?.split('@')[0] || 'currentUser';
  const name = currentUser?.displayName ? `${currentUser.displayName} (tú)` : 'Tú';
  const initials = currentUser?.displayName
    ? currentUser.displayName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : 'TU';
  return {
    user: {
      id: 1,
      login,
      name,
      initials,
      color: 'var(--z-accent)',
    },
    state: 'Aprobado',
    isCurrentUser: true,
  };
}

export function isCurrentUserReviewer(
  reviewer: GitHubReviewer,
  currentUser?: CurrentUserIdentity,
): boolean {
  if (reviewer.isCurrentUser === true) return true;
  return isMatchingUser(reviewer.user, currentUser);
}

export function matchesGitHubItem(item: AttentionItem, itemId: string): boolean {
  if (item.source !== 'github') return false;
  const raw = String(itemId).trim();
  if (item.id === raw) return true;
  // Live ids are owner~repo~number (or legacy owner/repo#number). Never fuzzy-match by number.
  if (item.id.includes('~') || item.id.includes('/')) return false;
  if (raw.includes('~') || raw.includes('/')) return false;
  const numeric = raw.replace(/^PR-/i, '').replace(/^#/, '');
  if (!/^\d+$/.test(numeric)) return false;
  return (
    item.primary === raw ||
    item.primary === `#${numeric}` ||
    item.id === `PR-${numeric}` ||
    item.id === raw
  );
}

export function upsertGitHubItem(prev: AttentionItem[], mapped: AttentionItem): AttentionItem[] {
  const idx = prev.findIndex((item) => matchesGitHubItem(item, mapped.id));
  if (idx === -1) {
    return [...prev, mapped];
  }
  const next = [...prev];
  next[idx] = {
    ...prev[idx],
    ...mapped,
    ai: mapped.ai || prev[idx].ai,
    next: mapped.next || prev[idx].next,
  };
  return next;
}

function prevJobId(
  states: Record<string, GitHubAiReviewState>,
  itemId: string,
): string | undefined {
  return states[itemId]?.job?.jobId;
}

export function mergeAiReviewJobState(
  previous: Record<string, GitHubAiReviewState>,
  job: AiReviewJob,
): Record<string, GitHubAiReviewState> {
  const existing = previous[job.prId] ?? { status: 'loading' as const };
  const progress = projectAiReviewProgress(job);
  if (job.status === 'completed' && job.review)
    return {
      ...previous,
      [job.prId]: {
        ...existing,
        status: 'ready',
        review: job.review,
        job,
        progress,
        statusRefreshError: undefined,
      },
    };
  if (job.status === 'completed')
    return {
      ...previous,
      [job.prId]: {
        ...existing,
        status: 'error',
        job,
        error: 'The review completed without a validated result.',
        progress,
        statusRefreshError: undefined,
      },
    };
  if (job.status === 'failed' || job.status === 'cancelled' || job.status === 'superseded')
    return {
      ...previous,
      [job.prId]: {
        ...existing,
        status: 'error',
        job,
        error:
          job.errorMessage ??
          (job.status === 'superseded' ? 'Review superseded by a newer run.' : undefined),
        progress,
        statusRefreshError: undefined,
      },
    };
  return {
    ...previous,
    [job.prId]: { ...existing, status: 'loading', job, progress, statusRefreshError: undefined },
  };
}

export function mergeListedAiReviewJobState(
  previous: Record<string, GitHubAiReviewState>,
  job: AiReviewJob,
): Record<string, GitHubAiReviewState> {
  const existing = previous[job.prId];
  if (existing?.job && existing.job.updatedAt > job.updatedAt) return previous;
  return mergeAiReviewJobState(previous, job);
}

function applyGeneratedAiReview(
  job: ReturnType<typeof toAiReviewJob>,
  setAiReviews: Dispatch<SetStateAction<Record<string, GitHubAiReviewState>>>,
): void {
  setAiReviews((prev) => ({
    ...mergeAiReviewJobState(prev, job),
  }));
  if (job.status === 'failed')
    throw Object.assign(new Error(job.errorMessage ?? 'La revisión no pudo completarse.'), {
      code: job.errorCode,
    });
}

export interface GitHubSliceOptions {
  currentUser?: CurrentUserIdentity;
  itemsRef: { current: AttentionItem[] };
  setItems: Dispatch<SetStateAction<AttentionItem[]>>;
  beginWrite: () => void;
  retry: (source?: string) => Promise<void>;
  setErrors?: Dispatch<SetStateAction<Record<string, string | null>>>;
}

function toAiReviewJob(
  itemId: string,
  data: Awaited<ReturnType<typeof gitHubClient.generateAiReview>>['data'],
) {
  if (!('findings' in data)) return data;
  const timestamp = new Date().toISOString();
  return {
    jobId: `legacy-${itemId}`,
    prId: itemId,
    status: 'completed' as const,
    stage: 'finalizing' as const,
    progressMessage: 'Revisión terminada.',
    review: data,
    createdAt: timestamp,
    updatedAt: timestamp,
  };
}

export function useGitHubSlice({
  currentUser,
  itemsRef,
  setItems,
  beginWrite,
  retry,
  setErrors,
}: GitHubSliceOptions) {
  const [rawPullRequests, setRawPullRequests] = useState<GitHubPullRequest[]>([]);
  const [aiReviews, setAiReviews] = useState<Record<string, GitHubAiReviewState>>({});
  const [itemSummaryBusy, setItemSummaryBusy] = useState<Record<string, boolean>>({});

  const refreshAiReviewJob = useCallback(async (jobId: string): Promise<void> => {
    try {
      const job = await getAiReviewJobWithRetry(gitHubClient, jobId);
      setAiReviews((prev) => mergeAiReviewJobState(prev, job));
    } catch (error) {
      const statusError: AiReviewStatusRefreshError = {
        code: AI_REVIEW_STATUS_REFRESH_FAILED,
        message: error instanceof Error ? error.message : AI_REVIEW_SYNC_UNAVAILABLE_MESSAGE,
      };
      setAiReviews((prev) => markAiReviewStatusRefreshError(prev, jobId, statusError));
    }
  }, []);

  const reconcileAiReviewJob = useCallback(
    async (resourceKey: string) => {
      const resource = decodeSourceSyncResourceKey(resourceKey);
      if (!resource || resource.resourceType !== 'job') return;
      await refreshAiReviewJob(resource.resourceId);
    },
    [refreshAiReviewJob],
  );

  const handleSourceSyncError = useCallback((error: { code: string; message: string }) => {
    setAiReviews((prev) =>
      markAiReviewStatusRefreshError(prev, undefined, {
        code: error.code as AiReviewStatusRefreshError['code'],
        message: error.message,
      }),
    );
  }, []);

  useEffect(() => {
    const unregisterReconciler = registerSourceSyncReconciler('aiReview', reconcileAiReviewJob);
    const unregisterErrorHandler = registerSourceSyncErrorHandler(
      'aiReview',
      handleSourceSyncError,
    );
    return () => {
      unregisterReconciler();
      unregisterErrorHandler();
    };
  }, [reconcileAiReviewJob, handleSourceSyncError]);

  useEffect(() => {
    let active = true;
    void gitHubClient
      .listAiReviewJobs()
      .then((response) => {
        if (!active) return;
        setAiReviews((previous) => {
          const next = { ...previous };
          for (const job of response.data) {
            Object.assign(next, mergeListedAiReviewJobState(next, job));
          }
          return next;
        });
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []);

  const activeJobIds = useMemo(() => activeAiReviewJobIds(aiReviews), [aiReviews]);
  const activeJobKey = activeJobIds.join(':');
  const activeJobIdsRef = useRef(activeJobIds);
  activeJobIdsRef.current = activeJobIds;
  useEffect(() => {
    if (!activeJobIdsRef.current.length) return;
    const refreshActiveJobs = (): void => {
      for (const jobId of activeJobIdsRef.current) {
        void refreshAiReviewJob(jobId).then(() => undefined);
      }
    };
    const timer = window.setInterval(refreshActiveJobs, AI_REVIEW_STATUS_POLL_INTERVAL_MS);
    refreshActiveJobs();
    return () => {
      window.clearInterval(timer);
    };
  }, [activeJobKey, refreshAiReviewJob]);

  const generateAiReview = useCallback(async (itemId: string, options?: { force?: boolean }) => {
    setAiReviews((prev) => ({
      ...prev,
      [itemId]: {
        ...(prev[itemId] ?? {}),
        status: 'loading',
        error: undefined,
        publishResult: undefined,
      },
    }));
    try {
      const res = await gitHubClient.generateAiReview(itemId, options);
      const job = toAiReviewJob(itemId, res.data);
      applyGeneratedAiReview(job, setAiReviews);
    } catch (err) {
      setAiReviews((prev) => ({
        ...prev,
        [itemId]: { ...(prev[itemId] ?? {}), status: 'error', error: aiReviewErrorMessage(err) },
      }));
    }
  }, []);

  const retryAiReviewStatus = useCallback(
    (itemId: string) => {
      const jobId = prevJobId(aiReviews, itemId);
      if (!jobId) return;
      void refreshAiReviewJob(jobId);
    },
    [aiReviews, refreshAiReviewJob],
  );

  const publishAiReview = useCallback(
    async (itemId: string, event: AiReviewPublishEvent) => {
      const review = itemsRefReview(aiReviews, itemId);
      if (!review) return;
      setAiReviews((prev) => ({
        ...prev,
        [itemId]: { ...prev[itemId], status: 'publishing', error: undefined },
      }));
      beginWrite();
      try {
        const res = await gitHubClient.publishAiReview(itemId, {
          event,
          review,
          ...(prevJobId(aiReviews, itemId) ? { jobId: prevJobId(aiReviews, itemId) } : {}),
        });
        setAiReviews((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], status: 'published', publishResult: res.data },
        }));
        await retry('github');
      } catch (err) {
        setAiReviews((prev) => ({
          ...prev,
          [itemId]: { ...prev[itemId], status: 'ready', error: aiReviewErrorMessage(err) },
        }));
      }
    },
    [aiReviews, beginWrite, retry],
  );

  /**
   * Plain-language "what does this PR say / need" briefing — distinct from
   * `aiReviews` (the code-review findings/approve/publish flow). Stored on
   * the item's shared `ai` field, the same convention Jira and Slack use for
   * their item summaries, so it can never collide with `aiReviews` state.
   */
  const generateItemSummary = useCallback(
    async (itemId: string, options?: { force?: boolean }) => {
      setItemSummaryBusy((prev) => ({ ...prev, [itemId]: true }));
      try {
        const res = await gitHubClient.generateItemSummary(itemId, options);
        setItems((prev) =>
          prev.map((entry) =>
            matchesGitHubItem(entry, itemId) ? { ...entry, ai: res.data.summary } : entry,
          ),
        );
      } catch {
        await retry('github');
      } finally {
        setItemSummaryBusy((prev) => ({ ...prev, [itemId]: false }));
      }
    },
    [setItems, retry],
  );

  const dismissAiReview = useCallback((itemId: string) => {
    setAiReviews((prev) => {
      if (!(itemId in prev)) return prev;
      const next = { ...prev };
      delete next[itemId];
      return next;
    });
  }, []);

  const toggleCheckRun = useCallback(
    async (itemId: string, index: number) => {
      beginWrite();
      setItems((prev) =>
        prev.map((item) => {
          if (matchesGitHubItem(item, itemId) && item.checks && item.checks[index]) {
            const updatedChecks = [...item.checks];
            updatedChecks[index] = {
              ...updatedChecks[index],
              conclusion: 'ok',
              status: 'completed',
            };
            return { ...item, checks: updatedChecks };
          }
          return item;
        }),
      );

      try {
        const res = await gitHubClient.retryCheckRun(itemId, index);
        setItems((prev) =>
          upsertGitHubItem(
            prev,
            mapGitHubPRToAttentionItem(res.data, undefined, undefined, undefined, currentUser),
          ),
        );
      } catch {
        await retry('github');
      }
    },
    [beginWrite, setItems, currentUser, retry],
  );

  const reRunAllChecks = useCallback(
    async (itemId: string) => {
      beginWrite();
      setItems((prev) =>
        prev.map((item) => {
          if (!matchesGitHubItem(item, itemId)) {
            return item;
          }
          const updatedChecks = (item.checks || []).map((c) => ({
            ...c,
            conclusion: 'ok' as const,
            status: 'completed' as const,
            title: 'Completado con éxito (re-ejecutado)',
            summaryLines: ['Todos los tests pasaron'],
          }));
          const isApproved = item.reviewers?.some((r) => r.state === 'Aprobado');
          return {
            ...item,
            checks: updatedChecks,
            st: isApproved ? 'ready' : item.st === 'blocked' ? 'you' : item.st,
            mergeTitle: isApproved ? 'Listo para mergear' : 'En revisión',
            mergeText: 'Todos los checks de CI/CD pasaron exitosamente.',
          };
        }),
      );

      try {
        const res = await gitHubClient.reRunAllChecks(itemId);
        setItems((prev) =>
          upsertGitHubItem(
            prev,
            mapGitHubPRToAttentionItem(res.data, undefined, undefined, undefined, currentUser),
          ),
        );
      } catch {
        await retry('github');
      }
    },
    [beginWrite, setItems, currentUser, retry],
  );

  const approvePullRequest = useCallback(
    async (itemId: string) => {
      beginWrite();
      setItems((prev) => applyOptimisticApproval(prev, itemId, currentUser));

      try {
        const res = await gitHubClient.approvePullRequest(itemId);
        setItems((prev) =>
          upsertGitHubItem(
            prev,
            mapGitHubPRToAttentionItem(res.data, undefined, undefined, undefined, currentUser),
          ),
        );
      } catch {
        await retry('github');
      }
    },
    [beginWrite, setItems, currentUser, retry],
  );

  const mergePullRequest = useCallback(
    async (itemId: string, commitMessage?: string, mergeStrategy?: GitHubMergeStrategy) => {
      beginWrite();
      setItems((prev) =>
        prev.map((item) => {
          if (!matchesGitHubItem(item, itemId)) {
            return item;
          }
          return {
            ...item,
            merged: true,
            st: 'ready',
            kind: 'Mergeado',
            mergeTitle: 'Mergeado en main',
            mergeText: commitMessage || 'Merge completado con éxito en la rama base.',
            action: 'Merge completado',
          };
        }),
      );

      try {
        const res = await gitHubClient.mergePullRequest(itemId, commitMessage, mergeStrategy);
        setItems((prev) =>
          upsertGitHubItem(
            prev,
            mapGitHubPRToAttentionItem(res.data, undefined, undefined, undefined, currentUser),
          ),
        );
      } catch {
        await retry('github');
      }
    },
    [beginWrite, setItems, currentUser, retry],
  );

  const postGitHubConversationComment = useCallback(
    async (itemId: string, markdown: string): Promise<void> => {
      beginWrite();
      try {
        await gitHubClient.postConversationComment(
          itemId,
          markdown,
          createIdempotencyKey('github-comment'),
        );
      } catch (error) {
        await retry('github').catch(() => undefined);
        throw error;
      }
      await retry('github').catch(() => undefined);
    },
    [beginWrite, retry],
  );

  const generateGitHubConversationCommentDraft = useCallback(
    async (itemId: string, hint?: string): Promise<string | null> => {
      setErrors?.((prev) => ({ ...prev, github: null }));
      try {
        const res = await gitHubClient.generateConversationCommentDraft(itemId, hint);
        return res.data.draft?.trim() || null;
      } catch (err) {
        setErrors?.((prev) => ({
          ...prev,
          github: err instanceof Error ? err.message : 'No se pudo generar el borrador con IA.',
        }));
        return null;
      }
    },
    [setErrors],
  );

  const toggleReviewCommentState = useCallback(
    async (itemId: string, index: number) => {
      beginWrite();
      const item = itemsRef.current.find((i) => matchesGitHubItem(i, itemId));
      const currentComment = item?.comments?.[index];
      const targetResolved =
        currentComment && currentComment.state !== 'Aprobado'
          ? currentComment.state !== 'Resuelto'
          : undefined;

      setItems((prev) => applyOptimisticCommentToggle(prev, itemId, index));

      try {
        const res = await gitHubClient.toggleCommentResolution(itemId, index, targetResolved);
        setItems((prev) =>
          upsertGitHubItem(
            prev,
            mapGitHubPRToAttentionItem(res.data, undefined, undefined, undefined, currentUser),
          ),
        );
      } catch {
        await retry('github');
      }
    },
    [beginWrite, itemsRef, setItems, currentUser, retry],
  );

  return useMemo(
    () => ({
      rawPullRequests,
      setRawPullRequests,
      toggleCheckRun,
      reRunAllChecks,
      approvePullRequest,
      mergePullRequest,
      postGitHubConversationComment,
      generateGitHubConversationCommentDraft,
      toggleReviewCommentState,
      aiReviews,
      generateAiReview,
      retryAiReviewStatus,
      publishAiReview,
      dismissAiReview,
      itemSummaryBusy,
      generateItemSummary,
    }),
    [
      rawPullRequests,
      toggleCheckRun,
      reRunAllChecks,
      approvePullRequest,
      mergePullRequest,
      postGitHubConversationComment,
      generateGitHubConversationCommentDraft,
      toggleReviewCommentState,
      aiReviews,
      generateAiReview,
      retryAiReviewStatus,
      publishAiReview,
      dismissAiReview,
      itemSummaryBusy,
      generateItemSummary,
    ],
  );
}

export type GitHubAiReviewStatus = 'loading' | 'ready' | 'publishing' | 'published' | 'error';

export interface GitHubAiReviewState {
  status: GitHubAiReviewStatus;
  review?: PullRequestAiReview;
  error?: string;
  publishResult?: PublishAiReviewResult;
  job?: AiReviewJob;
  progress?: import('../../domain/aiReviewProgress').AiReviewProgress;
  statusRefreshError?: AiReviewStatusRefreshError;
}

export function markAiReviewStatusRefreshError(
  previous: Record<string, GitHubAiReviewState>,
  jobId: string | undefined,
  error: AiReviewStatusRefreshError,
): Record<string, GitHubAiReviewState> {
  let changed = false;
  const next = Object.fromEntries(
    Object.entries(previous).map(([itemId, state]) => {
      if (!state.job || state.status !== 'loading' || (jobId && state.job.jobId !== jobId))
        return [itemId, state];
      changed = true;
      return [
        itemId,
        {
          ...state,
          statusRefreshError: error,
          progress: projectAiReviewProgress(state.job, error),
        },
      ];
    }),
  );
  return changed ? next : previous;
}

function itemsRefReview(
  reviews: Record<string, GitHubAiReviewState>,
  itemId: string,
): PullRequestAiReview | undefined {
  return reviews[itemId]?.review;
}

/** Turn API envelopes / thrown errors into one short, user-facing Spanish message. */
export function aiReviewErrorMessage(err: unknown): string {
  const code = (err as { code?: string } | undefined)?.code ?? '';
  if (code === 'AI_NOT_CONFIGURED')
    return 'Configura una clave de IA en Settings para revisar PRs.';
  if (code === 'GITHUB_TOKEN_MISSING') return 'Configura tu token de GitHub en Settings.';
  if (code === 'GITHUB_REVIEW_EMPTY_DIFF')
    return 'Este PR no tiene cambios revisables (solo archivos generados o binarios).';
  if (code === 'AI_RATE_LIMITED')
    // A 429 is either momentary saturation or an exhausted quota/spending cap;
    // "try again in a few seconds" is wrong for the second, so say both.
    return 'El proveedor de IA está limitado: saturación momentánea o cuota agotada. Si persiste, revisa el plan del proveedor.';
  if (code === 'GITHUB_REVIEW_STALE')
    return 'El PR cambió desde esta revisión. Regenera antes de publicar.';
  if (code === 'AI_REVIEW_INVALID_OUTPUT')
    return 'La IA devolvió una respuesta que no se pudo interpretar. Inténtalo de nuevo.';
  if (code === 'AI_PROVIDER_AUTH_FAILED')
    return 'La clave del proveedor de IA fue rechazada. Revísala en Settings.';
  if (code === 'AI_PROVIDER_TIMEOUT')
    // Distinct from a 429 on purpose: the route was working and ran out of
    // time, so "cuota agotada" sent people to a dashboard with quota to
    // spare. A diff this size needs a faster model, not a retry.
    return 'La IA tardó demasiado en revisar este PR. Elige un modelo más rápido para «Revisión de pull requests» en Settings › IA.';
  if (code === 'AI_PROVIDER_FAILED')
    return 'El proveedor de IA no está disponible ahora mismo. Inténtalo de nuevo en breve.';
  const message = err instanceof Error ? err.message : '';
  return message || 'No se pudo generar la revisión.';
}
